<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useFormValidation } from '../../composables/useFormValidation';
import { email, required } from '../../utils/validation';
import { getErrorMessage } from '../../utils/errorMessages';
import { useAuth } from '../../composables/useAuth';

defineEmits<{
  (e: 'forgotPassword'): void
  (e: 'register'): void
  (e: 'close'): void
}>()

const { login } = useAuth()
const { fields, formData, setFieldRules, validateForm } = useFormValidation({
  email: '',
  password: ''
})

const error = ref('')
const isLoading = ref(false)

// Set validation rules
onMounted(() => {
  setFieldRules('email', [required('Email is required'), email()])
  setFieldRules('password', [required('Password is required')])
})

const handleSubmit = async () => {
  if (!validateForm()) return
  
  error.value = ''
  isLoading.value = true
  
  try {
    await login(formData.value.email || '', formData.value.password || '')
  } catch (err: any) {
    error.value = getErrorMessage(err)
  } finally {
    isLoading.value = false
  }
}

</script>

<template>
  <div class="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg">
    <button 
        @click="$emit('close')"
        class="absolute -top-2 -right-2 w-8 h-8 flex items-center justify-center bg-white dark:bg-gray-800 rounded-full shadow-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors z-10"
      >
        ✕
      </button>
    <div class="text-center">
      <h2 class="text-3xl font-bold text-gray-900 dark:text-white">Sign In</h2>
      <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
        Welcome back! Please sign in to continue.
      </p>
    </div>

    <form @submit.prevent="handleSubmit" class="mt-8 space-y-6" novalidate>
      <!-- Error Alert -->
      <div 
        v-if="error"
        class="p-4 rounded-md bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800"
      >
        <p class="text-sm text-red-800 dark:text-red-200">{{ error }}</p>
      </div>

      <div class="space-y-4">
        <!-- Email Field -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Email address
          </label>
          <div class="relative">
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span class="text-gray-500 dark:text-gray-400">📧</span>
            </div>
            <input
              v-model="fields.email.value"
              type="email"
              placeholder="Enter your email"
              :class="[
                'w-full pl-10 pr-4 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm transition-colors',
                fields.email.touched && fields.email.error
                  ? 'border-red-300 dark:border-red-700'
                  : 'border-gray-300 dark:border-gray-600'
              ]"
              @blur="fields.email.touched = true"
            />
          </div>
          <p v-if="fields.email.touched && fields.email.error" class="mt-1 text-sm text-red-600 dark:text-red-400">
            {{ fields.email.error }}
          </p>
        </div>

        <!-- Password Field -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Password
          </label>
          <div class="relative">
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span class="text-gray-500 dark:text-gray-400">🔒</span>
            </div>
            <input
              v-model="fields.password.value"
              type="password"
              placeholder="Enter your password"
              :class="[
                'w-full pl-10 pr-4 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm transition-colors',
                fields.password.touched && fields.password.error
                  ? 'border-red-300 dark:border-red-700'
                  : 'border-gray-300 dark:border-gray-600'
              ]"
              @blur="fields.password.touched = true"
            />
          </div>
          <p v-if="fields.password.touched && fields.password.error" class="mt-1 text-sm text-red-600 dark:text-red-400">
            {{ fields.password.error }}
          </p>
        </div>
      </div>

      <div class="flex justify-between items-center">
        <button
          type="button"
          @click="$emit('forgotPassword')"
          class="text-sm font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
        >
          Forgot password?
        </button>
      </div>

      <button
        type="submit"
        :disabled="isLoading"
        class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span v-if="isLoading">Signing in...</span>
        <span v-else>Sign in</span>
      </button>

      <div class="text-center">
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Don't have an account?
          <button
            type="button"
            @click="$emit('register')"
            class="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
          >
            Register now
          </button>
        </p>
      </div>
    </form>
  </div>
</template>